// web/src/lib/deliverVideo.ts
// Delivery of the exported MP4: native share sheet on touch devices (Photos /
// iMessage / AirDrop), plain download everywhere else. The touch gate is
// `(pointer: coarse)` — the same signal SourceSlot's "My Phone" label and the
// player's auto-fullscreen use — because desktop Chrome technically canShare but
// the product decision is share-on-mobile, download-on-desktop.
import { downloadBlob } from "./download";

export type DeliverOutcome = "shared" | "downloaded" | "cancelled";

// TS's DOM lib types Navigator.canShare/share as always-present (Web Share API), but the
// feature is real-world optional (desktop Chrome, older Safari, etc. don't implement it) —
// hence the runtime `typeof ... === "function"` guards below despite the "guaranteed" type.
const PROBE_FILE = () => new File([new Blob(["p"], { type: "video/mp4" })], "probe.mp4", { type: "video/mp4" });

export function canShareVideo(
  nav: Navigator = navigator,
  matchMediaFn: typeof window.matchMedia | undefined = typeof window !== "undefined" ? window.matchMedia.bind(window) : undefined,
): boolean {
  try {
    if (!matchMediaFn?.("(pointer: coarse)").matches) return false;
    if (typeof nav.canShare !== "function" || typeof nav.share !== "function") return false;
    return nav.canShare({ files: [PROBE_FILE()] });
  } catch { return false; }
}

/** Share (mobile) or download (fallback/desktop). "cancelled" = user closed the sheet —
 *  callers must NOT latch the leave guard on that outcome. */
export async function deliverVideo(
  blob: Blob,
  filename: string,
  nav: Navigator = navigator,
  matchMediaFn?: typeof window.matchMedia,
): Promise<DeliverOutcome> {
  if (canShareVideo(nav, matchMediaFn ?? (typeof window !== "undefined" ? window.matchMedia.bind(window) : undefined))) {
    try {
      await nav.share({ files: [new File([blob], filename, { type: "video/mp4" })] });
      return "shared";
    } catch (e) {
      if ((e as { name?: string } | null)?.name === "AbortError") return "cancelled";
      // share broke for a non-user reason — the file must still be obtainable
    }
  }
  downloadBlob(blob, filename);
  return "downloaded";
}
